import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Segmented, Button } from '../../../../../../components';
import { InventoryDrawerDetails } from './InventoryDrawerDetails';
import { Settings } from './Settings/Settings';
import {
    useAddRemoveIgnoredOptimizationsMutation,
    useUpdateOptimizationsMutation,
} from '../../../../../../api/discovery/inventory';
import { useNotificationContext } from '../../../../../../providers/NotificationProvider';
import { deepEqual } from '../../../../../../lib/helpers';
import { OPTIMIZATION_TYPES } from '../constants';
import { hasOptimizations } from '../../../../helpers';

import './DetailsAndSettings.scss';

export const DetailsAndSettings = ({ record, scrollContainerRef }) => {
    const [view, setView] = useState('Details');
    const orgId = useMemo(() => sessionStorage.getItem('userOrg'), []);
    const [addRemoveIgnoredOptimizations, { isLoading }] = useAddRemoveIgnoredOptimizationsMutation();
    const [updateOptimizations, { isLoading: isUpdatingOptimizations }] = useUpdateOptimizationsMutation();
    const { notifySuccess, notifyError } = useNotificationContext();

    const defaultOptimizations = useMemo(
        () => ({
            cpu: record?.ignore_optimizations.cpu || [],
            ram: record?.ignore_optimizations.ram || [],
            modernization: record?.ignore_optimizations.modernization || [],
            storage: record?.ignore_optimizations.storage || [],
        }),
        [record],
    );

    // Additional Optimizations default values from the record
    const defaultAdditionalOptimizations = useMemo(
        () => ({
            optimizations: {
                cpu: record?.optimizations?.cpu || '',
                sockets: record?.optimizations?.sockets || '',
                cores: record?.optimizations?.cores || '',
                ram: record?.optimizations?.ram || '',
                storage: {
                    mounts: record?.optimizations?.storage?.mounts || null,
                    disks: record?.optimizations?.storage?.disks || null,
                },
                osm: record?.optimizations?.osm || '',
                aws: {
                    instance_type: record?.optimizations?.aws?.instance_type || '',
                },
            },
            id: record?.id,
        }),
        [record],
    );

    // Optimization state initially populated from the record
    // This will be udpated depending on what the user changes in the Compute, Storage, OS sections
    const [ignoredOptimizations, setIgnoredOptimizations] = useState(defaultOptimizations);
    const [additionalOptimizations, setAdditionalOptimizations] = useState(defaultAdditionalOptimizations);

    // When the user views a different record OR when the record data changes,
    // update the original ignored optimizations and the state
    useEffect(() => {
        // Also reset the state when a new record is selected or updated
        setIgnoredOptimizations(defaultOptimizations);
        setAdditionalOptimizations(defaultAdditionalOptimizations);
    }, [defaultOptimizations, defaultAdditionalOptimizations]);

    const revertAll = useCallback(async () => {
        await addRemoveIgnoredOptimizations({
            orgId,
            vmIds: [record?.id],
            action: 'remove',
            optimizations: {
                cpu: [OPTIMIZATION_TYPES.UNDERSIZED, OPTIMIZATION_TYPES.OVERSIZED],
                ram: [OPTIMIZATION_TYPES.OVERSIZED, OPTIMIZATION_TYPES.UNDERSIZED],
                modernization: [OPTIMIZATION_TYPES.OSM, OPTIMIZATION_TYPES.SQLM],
                storage: [OPTIMIZATION_TYPES.UNDERSIZED, OPTIMIZATION_TYPES.OVERSIZED],
            },
        }).unwrap();

        await updateOptimizations({
            orgId,
            vmId: record?.id,
            optimizations: {},
        }).unwrap();

        notifySuccess({ message: 'Optimizations successfully reverted' });
    }, [addRemoveIgnoredOptimizations, updateOptimizations, orgId, record, notifySuccess]);

    // Calculate what ignored optimizations to add and what to remove
    const { toAdd, toRemove } = useMemo(() => {
        const toAdd = {};
        const toRemove = {};

        // Get the original optimizations from the ref, compare with current state
        Object.keys(ignoredOptimizations).forEach((key) => {
            const original = defaultOptimizations[key];
            const current = ignoredOptimizations[key];

            // Find additions (in current but not in original)
            const additions = current.filter((opt) => !original.includes(opt));
            // If there are additions, send the current state for that key, the API overwrites, not merges
            if (additions.length > 0) {
                toAdd[key] = current;
            }

            // Find removals (in original but not in current)
            const removals = original.filter((opt) => !current.includes(opt));
            // If there are removals, send the original state for that key, the API overwrites, not merges
            if (removals.length > 0) {
                toRemove[key] = original;
            }
        });

        return { toAdd, toRemove };
    }, [ignoredOptimizations]);

    const hasChanges = useMemo(() => {
        const hasIgnoredChanges = Object.keys(toAdd).length > 0 || Object.keys(toRemove).length > 0;
        const hasAdditionalChanges = !deepEqual(additionalOptimizations, defaultAdditionalOptimizations);
        return hasIgnoredChanges || hasAdditionalChanges;
    }, [toAdd, toRemove, additionalOptimizations, defaultAdditionalOptimizations]);

    const applyOptimizations = useCallback(async () => {
        try {
            // We need to send two requests becuase the endpoint only supports either addition or removal at once
            // Only send add request if there are additions
            if (Object.keys(toAdd).length > 0) {
                await addRemoveIgnoredOptimizations({
                    orgId,
                    vmIds: [record?.id],
                    action: 'add',
                    optimizations: toAdd,
                }).unwrap();
            }
            // Only send remove request if there are removals
            if (Object.keys(toRemove).length > 0) {
                await addRemoveIgnoredOptimizations({
                    orgId,
                    vmIds: [record?.id],
                    action: 'remove',
                    optimizations: toRemove,
                }).unwrap();
            }

            // Send additional optimizations if there are any changes to optimizations or ignored optimizations
            const hasIgnoredChanges = Object.keys(toAdd).length > 0 || Object.keys(toRemove).length > 0;
            if (!deepEqual(additionalOptimizations, defaultAdditionalOptimizations) || hasIgnoredChanges) {
                await updateOptimizations({
                    orgId,
                    vmId: record?.id,
                    optimizations: {
                        ...additionalOptimizations.optimizations,
                        ignored_optimizations: {
                            ...ignoredOptimizations,
                            ...(additionalOptimizations.optimizations.aws.instance_type === '' && {
                                aws: { instance_type: true },
                            }),
                        },
                    },
                }).unwrap();
            }

            notifySuccess({ message: 'Optimizations successfully applied' });
        } catch (error) {
            notifyError('Error', 'Failed to update optimizations.');
        }
    }, [
        toAdd,
        toRemove,
        addRemoveIgnoredOptimizations,
        updateOptimizations,
        additionalOptimizations,
        orgId,
        record?.id,
    ]);

    return (
        <>
            <div className="discovery-inventory-details-and-settings">
                <header>
                    <Segmented options={['Details', 'Settings']} onChange={setView} shape="round" />
                </header>
                {view === 'Details' ? (
                    <InventoryDrawerDetails record={record} />
                ) : (
                    <Settings
                        record={record}
                        ignoredOptimizations={ignoredOptimizations}
                        setIgnoredOptimizations={setIgnoredOptimizations}
                        additionalOptimizations={additionalOptimizations}
                        setAdditionalOptimizations={setAdditionalOptimizations}
                        isLoading={isLoading || isUpdatingOptimizations}
                        scrollContainerRef={scrollContainerRef}
                    />
                )}
            </div>
            {view === 'Settings' && (
                <footer className="settings-footer">
                    <Button
                        type="default"
                        shape="round"
                        disabled={
                            !hasOptimizations(record?.optimizations) && !hasOptimizations(record?.ignore_optimizations)
                        }
                        onClick={revertAll}
                    >
                        Revert All
                    </Button>
                    <Button type="primary" shape="round" disabled={!hasChanges} onClick={applyOptimizations}>
                        Apply All
                    </Button>
                </footer>
            )}
        </>
    );
};
